"use client";
import React, { useState, useMemo, useRef, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import CalcInputValues from "../ui/CalcInputValues";
import { formatCompactNumber, formatIndianNumber } from "@/utils/commonFunctions";
import { useIsMobile } from "@/utils/useIsMobile";
import CustomModal from "@/components/modal/CustomModal";
import Image from "next/image";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import StartSIPStrip from "../strips/StartSIPStrip";
import { calculateStepUpSIP, calculatoeNormalSipCalc } from "@/utils/allCalculators";
import { useLoginModal } from "@/utils/useLoginModal";
import { ROOT_URL } from "@/utils/gloableVariables";

export type NormalSipResult = {
  totalInvestment: string;
  futureValue: string;
  profitValue: string;
};

export type StepUpReportRow = {
  tenure: number;
  annualInvestment: string;
  totalInvestment: string;
  currentBalance: string;
  profitAmt: string;
  totalProfitAmt: string;
};

export type StepUpResult = {
  stepupReport: StepUpReportRow[];
  totalInvestment: string;
  currentBalance: string;
  totalReturns: string;
  graphData: {
    investment: number[];
    current: number[];
    tenure: number[];
  };
};

export default function StepUpCalc() {
  const [isOpen, setIsOpen] = useState(false);
  const [initialAmount, setInitailAmount] = useState(10000);
  const [years, setYears] = useState(5);
  const [rate, setRate] = useState(15);
  const [investmentType, setInvestmentType] = useState<"Monthly" | "Annually">("Monthly");
  const [incrementType, setIncrementType] = useState<"₹" | "%">("₹");
  const [incrementAmount, setIncrementAmount] = useState(1000);

  const reportRef = useRef<HTMLDivElement>(null);

  const { isLoggedIn, open, setModalKey } = useLoginModal();

  const [calculation, setCalculation] = useState<StepUpResult | null>(null);
  const [normalSip, setNormalSip] = useState<NormalSipResult | null>(null);
  const isTab = useIsMobile(1025);
  const isMob = useIsMobile(769);

  const handleLogin = () => {
    open();
    setModalKey("step-up-calculator");
  };

  useEffect(() => {
    const result = calculateStepUpSIP(initialAmount, incrementAmount, years, rate, investmentType, incrementType);
    setCalculation(result);

    const normalSIP = calculatoeNormalSipCalc(initialAmount, years, rate, investmentType);
    setNormalSip(normalSIP);
  }, [initialAmount, incrementAmount, years, rate, investmentType, incrementType]);

  const handleExcelDownload = () => {
    if (!isLoggedIn) return handleLogin();

    const data =
      calculation?.stepupReport.map((row) => ({
        tenure: row.tenure,
        annualInvestment: formatIndianNumber(Number(row.annualInvestment).toFixed(0)),
        totalInvestment: formatIndianNumber(Number(row.totalInvestment).toFixed(0)),
        currentBalance: formatIndianNumber(Number(row.currentBalance).toFixed(0)),
        profitAmt: formatIndianNumber(Number(row.profitAmt).toFixed(0)),
        totalProfitAmt: formatIndianNumber(Number(row.totalProfitAmt).toFixed(0)),
      })) ?? [];

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Step Up Report");
    XLSX.writeFile(workbook, "Step_Up_Investment_Report.xlsx");
  };

  const handlePDFDownload = () => {
    if (!isLoggedIn) return handleLogin();

    const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "p" });
    const { pageSize } = pdf.internal;
    const pageWidth = pageSize.getWidth();
    const pageHeight = pageSize.getHeight();

    // Layout
    const layout = { marginX: 12, headerY: 12, titleY: 18, headerArea: 22, footer: 12, rowH: 10 };
    const usableWidth = pageWidth - layout.marginX * 2;

    // Headers + column widths
    const headers = [investmentType === "Monthly" ? "Months" : "Years", "Deposits", "Interest", "Total Deposits", "Accrued Interest", "Current Balance"];

    const colWidths = [0.12, 0.18, 0.13, 0.18, 0.19, 0.2].map((p) => p * usableWidth);

    // Helpers
    const centerText = (text: string, x: number, y: number, opts = {}) => pdf.text(text, x, y, { align: "center", baseline: "middle", ...opts });

    const drawHeader = (first = false) => {
      const y = layout.headerY;

      pdf.setFontSize(22).setTextColor(202, 202, 202).text("MySIPonline", layout.marginX, y);

      if (first) {
        const title = "Step Up Report";
        pdf
          .setFontSize(18)
          .setTextColor(0)
          .text(title, pageWidth / 2, y, { align: "center" });
      }
    };

    const drawTableHeader = (y: number) => {
      let x = layout.marginX;
      headers.forEach((h, i) => {
        pdf.setFontSize(11).setTextColor(255, 255, 255).setFillColor(16, 188, 157).setDrawColor(217, 217, 217);

        pdf.rect(x, y, colWidths[i], layout.rowH, "FD"); // Fill + Border
        centerText(h, x + colWidths[i] / 2, y + layout.rowH / 2);
        x += colWidths[i];
      });
    };

    const drawRow = (y: number, values: (string | number)[]) => {
      let x = layout.marginX;
      pdf.setFontSize(10).setTextColor(99, 89, 90).setDrawColor(217, 217, 217);
      values.forEach((val, i) => {
        pdf.rect(x, y, colWidths[i], layout.rowH);
        centerText(String(val), x + colWidths[i] / 2, y + layout.rowH / 2);
        x += colWidths[i];
      });
    };

    const drawFooter = (pages: number) => {
      for (let p = 1; p <= pages; p++) {
        pdf.setPage(p);
        const footerY = pageHeight - 8;
        pdf.setFontSize(12).setTextColor(0).text(`Page ${p}/${pages}`, layout.marginX, footerY);

        const prefix = "Report by ";
        const w = pdf.getTextWidth(prefix);
        pdf.text(prefix, pageWidth - layout.marginX - w - 40, footerY);

        pdf.setTextColor(0, 102, 153).textWithLink("MySIPonline", pageWidth - layout.marginX - 38, footerY, { url: "https://mysiponline.com" });
      }
    };

    // === Build PDF ===
    let y = layout.headerArea;
    drawHeader(true);
    drawTableHeader(y);
    y += layout.rowH;

    for (const row of calculation?.stepupReport ?? []) {
      const values = [row.tenure, formatIndianNumber(+row.annualInvestment), formatIndianNumber(+row.profitAmt), formatIndianNumber(+row.totalInvestment), formatIndianNumber(+row.totalProfitAmt), formatIndianNumber(+row.currentBalance)];

      if (y + layout.rowH > pageHeight - layout.footer) {
        pdf.addPage();
        drawHeader();
        y = layout.headerArea + 4;
        drawTableHeader(y);
        y += layout.rowH;
      }

      drawRow(y, values);
      y += layout.rowH;
    }

    drawFooter(pdf.internal.pages.length - 1);
    pdf.save("Step_Up_Report.pdf");
  };

  const handleIncrementTpye = (newType: "₹" | "%") => {
    if (newType === "₹") {
      setIncrementAmount(1000);
    } else {
      setIncrementAmount(5);
    }
    setIncrementType(newType);
  };

  const handleScrollReport = () => {
    if (reportRef.current) {
      const headerOffset = 30;
      const elementPosition = reportRef.current.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const handleRightPart = () => {
    if (!isTab) return;
    setIsOpen(true);
  };

  const displayedData = useMemo(() => {
    if (isLoggedIn) return calculation?.stepupReport ?? [];

    const realRows = (calculation?.stepupReport ?? []).slice(0, 2);

    const fakeCount = Math.max(5 - realRows.length, 0);

    const startYear = realRows.length > 0 ? realRows.length + 1 : 1;

    const fakeRows = Array.from({ length: fakeCount }, (_, i) => ({
      tenure: `${startYear + i}`,
      annualInvestment: 1000,
      totalInvestment: 1000,
      currentBalance: 1000,
      profitAmt: 1000,
      totalProfitAmt: 1000,
      isFake: true,
    }));

    return [...realRows, ...fakeRows];
  }, [isLoggedIn, calculation?.stepupReport]);

  return (
    <>
      {/* Hero Heading */}
      <section className="section px-3 sm:px-6 lg:px-4 mb-12 sm:mb-100">
        {/* Floating Calculator */}
        <div className="w-full relative text-white xs:bg-white xs:rounded-xl xs:shadow-lg sm:overflow-hidden xs:p-6">
          <div className="flex flex-col lg:flex-row gap-3.5 2lg:gap-6 items-center w-full bg-white text-black rounded-xl shadow-lg xs:rounded-none xs:shadow-none p-4 sm:p-0 lg:h-142">
            {/* Inputs */}
            <div className="w-full lg:w-[47%] 2lg:w-[40%] shrink-0">
              <div className="space-y-8">
                <CalcInputValues label="I want to invest" min={100} max={100000} step={500} unit="₹" amountValue={initialAmount} modeOptions={["Monthly", "Annually"]} defaultMode="Monthly" onModeChange={(mode) => setInvestmentType(mode as "Monthly" | "Annually")} showFloatingValue unitPosition="left" onChange={setInitailAmount} />

                <CalcInputValues key={incrementType} label={`Increase SIP (by ${incrementType} P.a.)`} min={incrementType == "₹" ? 100 : 5} max={incrementType == "₹" ? 100000 : 50} unit={incrementType} amountValue={incrementType === "₹" ? 1000 : 5} unitPosition={incrementType == "₹" ? "left" : "right"} onChange={setIncrementAmount} showAmountModeToggle amountMode={incrementType} onAmountModeChange={(e) => handleIncrementTpye(e)} />

                <CalcInputValues label="Over a period of (Years)" min={1} max={50} unit="Years" amountValue={years} onChange={setYears} />
                <CalcInputValues label="Expected Rate of Return(% P.a.)" min={1} max={30} unit="%" allowDecimal amountValue={rate} onChange={setRate} />
              </div>

              <button onClick={handleRightPart} className="lg:hidden block mt-4 w-full max-w-fit mx-auto bg-primaryGreen rounded-xl text-sm font-medium text-white px-8 xs:px-14 py-2 xs:py-2.5">
                Calculate
              </button>
            </div>

            {!isTab && <RightPart className="hidden lg:block" calculation={calculation!} normalSip={normalSip!} />}
          </div>
          {!isMob && <StartSIPStrip className="hidden sm:flex" />}
        </div>
      </section>

      <section className="section mb-half-px sm:hidden">{isMob && <StartSIPStrip className="sm:hidden" />}</section>

      {/* Detailed Report (margin auto from calc height) */}
      <section ref={reportRef} className="section mb-half-px sm:mb-100">
        <div className="flex items-center gap-2 xxxs:gap-3 mb-3">
          <h2 className="text-lg sm:text-2xl lg:text-3xl font-medium">Detailed Report</h2>

          <span className="ml-auto text-sm sm:text-base text-[#004b6b]">Download</span>

          <button onClick={handleExcelDownload} aria-label="Excel File Download Button" className="border border-[#56565640] rounded-lg px-3 py-2 hover:border-[#56565680]">
            <Image src="https://www.mysiponline.com/images/swp-calculator/excel.svg" alt="strip-image" width={24} height={24} />
          </button>

          <button onClick={handlePDFDownload} aria-label="PDF Download Button" className="border border-[#56565640] rounded-lg px-3 py-2 hover:border-[#56565680]">
            <Image src="https://www.mysiponline.com/images/swp-calculator/pdf.svg" alt="strip-image" width={24} height={24} />
          </button>
        </div>

        <div className="rounded-2xl overflow-hidden">
          <div className={`${isLoggedIn ? "overflow-auto" : "overflow-hidden"} relative rounded-2xl sm:px-5 max-h-141 scrollbar-xs sm:scrollbar-md border-2 border-primaryBlack`}>
            <table className="w-full text-left border-collapse">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="border-none">
                  <th className="p-0 sticky left-0 ">
                    <p className="p-4 whitespace-nowrap text-2xs sm:text-2sm lg:text-xl sm:border-b-2 border-primaryBlack font-semibold text-primaryBlack text-center bg-[#f2f2f2] sm:bg-white">{investmentType === "Monthly" ? "Months" : "Years"}</p>
                  </th>
                  <th className="p-0">
                    <p className="p-4 whitespace-nowrap text-2xs sm:text-2sm lg:text-xl sm:border-b-2 border-primaryBlack font-semibold text-primaryBlack text-center bg-[#f2f2f2] sm:bg-white">Deposits</p>
                  </th>
                  <th className="p-0">
                    <p className="p-4 whitespace-nowrap text-2xs sm:text-2sm lg:text-xl sm:border-b-2 border-primaryBlack font-semibold text-primaryBlack text-center bg-[#f2f2f2] sm:bg-white">Interest</p>
                  </th>
                  <th className="p-0">
                    <p className="p-4 whitespace-nowrap text-2xs sm:text-2sm lg:text-xl sm:border-b-2 border-primaryBlack font-semibold text-primaryBlack text-center bg-[#f2f2f2] sm:bg-white">Total Deposits</p>
                  </th>
                  <th className="p-0">
                    <p className="p-4 whitespace-nowrap text-2xs sm:text-2sm lg:text-xl sm:border-b-2 border-primaryBlack font-semibold text-primaryBlack text-center bg-[#f2f2f2] sm:bg-white">Accrued Interest</p>
                  </th>
                  <th className="p-0">
                    <p className="p-4 whitespace-nowrap text-2xs sm:text-2sm lg:text-xl sm:border-b-2 border-primaryBlack font-semibold text-primaryBlack text-center bg-[#f2f2f2] sm:bg-white">Current Balance</p>
                  </th>
                </tr>
              </thead>

              <tbody className="bg-white">
                {displayedData.map((row) => (
                  <tr key={row.tenure} className="border-b border-[#cacaca] border-dashed last:border-0">
                    <td className="px-4 py-3 text-center text-xs sm:text-2sm text-primaryBlack sticky left-0 bg-[#f2f2f2] sm:bg-white">{row.tenure}</td>

                    <td className="px-4 py-3 text-center text-xs sm:text-2sm text-primaryBlack">{formatIndianNumber(Number(row.annualInvestment).toFixed(0), { prefix: "₹" })}</td>

                    <td className="px-4 py-3 text-center text-xs sm:text-2sm text-primaryBlack">{formatIndianNumber(Number(row.profitAmt).toFixed(0), { prefix: "₹" })}</td>

                    <td className="px-4 py-3 text-center text-xs sm:text-2sm text-primaryBlack">{formatIndianNumber(Number(row.totalInvestment).toFixed(0), { prefix: "₹" })}</td>

                    <td className="px-4 py-3 text-center text-xs sm:text-2sm text-primaryBlack">{formatIndianNumber(Number(row.totalProfitAmt).toFixed(0), { prefix: "₹" })}</td>

                    <td className="px-4 py-3 text-center text-xs sm:text-2sm text-primaryBlack">{formatIndianNumber(Number(row.currentBalance).toFixed(0), { prefix: "₹" })}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {!isLoggedIn && (
              <div className="absolute inset-0 top-20 bottom-1 xs:bottom-0 xs:top-24 z-1 mx-auto max-w-[98%] bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,rgba(255,255,255,0.39)_41.78%)] flex items-center justify-center text-center text-sm sm:text-base font-medium text-primaryBlack px-6">
                <div className="gradient-blur rounded-2xl">
                  <div /> <div /> <div /> <div /> <div /> <div />
                </div>

                <div className="relative z-1 bg-white xs:w-4/5 xl:w-2/3 h-fit border border-[#e6e6e6] shadow-[0px_4px_4px_0px_#00000026] flex flex-col sm:flex-row items-center p-4 xxxs:p-5 md:pl-10 rounded-md overflow-hidden gap-2 sm:gap-0">
                  <Image className="absolute z-0 top-0 left-0 bottom-0" src="https://www.mysiponline.com/images/swp-calculator/curves.svg" alt="curves-image" width={60} height={50} />
                  <p className="flex items-center gap-2 mx-auto xs:mx-0">
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" className="relative z-1 w-4 h-4 xs:w-7 xs:h-7">
                      <rect width="28" height="28" rx="14" fill="#1A51AA" />
                      <path
                        d="M14.3334 11.3334C13.6258 11.3334 12.952 11.474 12.3334 11.7246V9.3334C12.3334 8.22918 13.2292 7.3334 14.3334 7.3334C15.4382 7.3334 16.3334 8.22918 16.3334 9.3334V10H17.6668V9.3334C17.6668 7.49219 16.1746 6 14.3334 6C12.4922 6 11 7.49219 11 9.3334V12.5072C9.78199 13.4844 9 14.983 9 16.6666C9 19.612 11.388 22 14.3334 22C17.2788 22 19.6668 19.612 19.6668 16.6666C19.6668 13.7212 17.2788 11.3334 14.3334 11.3334ZM15 17.1484V18.6666H13.6668V17.1484C13.2696 16.918 13 16.4922 13 16C13 15.2642 13.597 14.6666 14.3334 14.6666C15.0698 14.6666 15.6668 15.2642 15.6668 16C15.6668 16.4922 15.3972 16.918 15 17.1484Z"
                        fill="white"
                      />
                    </svg>

                    <span className="whitespace-nowrap relative z-1 text-sm xs:text-base sm:text-lg text-primaryBlue font-medium">Sign in to Unlock Premium Details</span>
                  </p>
                  <button onClick={handleLogin} className="bg-transparent border-2 text-2xs sm:text-base border-primaryBlue text-primaryBlue rounded-full font-medium mx-auto sm:mr-0 sm:ml-auto py-1 px-10 w-fit transition duration-300 hover:bg-[#CAE7FF]">
                    Unlock Now
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {isTab && (
        <CustomModal isOpen={isOpen} onClose={() => setIsOpen(false)} maxSize="max-w-[80%]" useDragToClose isBlurOverlay className="bg-white rounded-b-none sm:rounded-4xl">
          <RightPart className="block lg:hidden" isOpen={isOpen} calculation={calculation!} normalSip={normalSip!} onClose={() => setIsOpen(false)} onScrollToReport={handleScrollReport} />
        </CustomModal>
      )}
    </>
  );
}

function RightPart({ className, isOpen, calculation, normalSip, onClose, onScrollToReport }: { isOpen?: boolean; className?: string; calculation: StepUpResult; normalSip: NormalSipResult; onClose?: () => void; onScrollToReport?: () => void }) {
  const isMob = useIsMobile();
  const [isChartLoading, setIsChartLoading] = useState(true);
  if (!calculation || !calculation.graphData || !normalSip) return null;

  const chartOptions: Highcharts.Options = {
    chart: {
      type: "line",
      height: isMob ? 250 : 300,
      spacing: [24, 10, 10, 10],
      style: { fontFamily: "Inter, sans-serif" },
      events: {
        load: () => {
          setIsChartLoading(false);
        },
      },
    },
    title: { text: undefined },
    xAxis: {
      categories: calculation.graphData.tenure.map(String),
      title: { text: "Investment Period (Years)", style: { fontSize: "12px", fontWeight: "500", color: "#666" } },
      labels: { style: { fontSize: "12px", fontWeight: "500", color: "#333" } },
    },
    yAxis: {
      min: 0,
      title: { text: "Value of Investment (₹)", style: { fontSize: "12px", fontWeight: "500", color: "#666" } },
      labels: {
        style: { fontSize: "12px", fontWeight: "500", color: "#333" },
        formatter: function () {
          return "₹" + formatCompactNumber(this.value as number, false);
        },
      },
    },
    tooltip: {
      useHTML: true,
      shared: true,
      formatter: function () {
        const invested = Math.round(this.points?.[0].y as number);
        const resultant = Math.round(this.points?.[1].y as number);

        return `
          <div class="bg-white">
            <div class="font-bold">Year ${this.key}</div>
            <div class="font-semibold text-[#8E44AD]"><span class="font-normal">Invested Amount:</span> ${formatIndianNumber(Number(invested), { prefix: "₹" })}</div>
            <div class="font-semibold text-[#16A085]"><span class="font-normal">Resultant Amount:</span> ${formatIndianNumber(Number(resultant), { prefix: "₹" })}</div>
          </div>`;
      },
    },
    credits: { enabled: false },
    legend: {
      useHTML: true,
      labelFormatter: function () {
        const dotColor = this.name === "Invested" ? "#8E44AD" : "#16A085";
        return `<div class="flex items-center gap-1">
                  <span class="w-3 h-3 inline-block rounded-full" style="background:${dotColor}"></span> ${this.name}
                </div>`;
      },
      symbolHeight: 0,
      symbolWidth: 0,
      symbolRadius: 0,
      align: "right",
      verticalAlign: "top",
      layout: "horizontal",
      x: -10,
      y: 0,
    },
    series: [
      { type: "line", name: "Invested", data: calculation.graphData.investment, color: "#8E44AD", marker: { enabled: false } },
      { type: "line", name: "Resultant", data: calculation.graphData.current, color: "#16A085", marker: { enabled: false } },
    ],
  };

  return (
    <>
      <div className={`w-full lg:w-[52%] 2lg:w-[58%] lg:border-2 border-[#006b6633] rounded-2xl p-5 pt-0 sm:p-6 sm:pt-0 lg:p-6 bg-white sm:bg-[#f9fbfb] ${className}`}>
        <div className="h-250 xs:h-300">
          {isChartLoading && <Image src="/next_assets/images/gif/chart_loading.gif" unoptimized alt="Loading Chart Animation" loading="eager" width={256} height={144} priority className="w-64 h-36 mx-auto mt-14" />}
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        </div>

        <div className="bg-white px-4 py-6 rounded-2xl mt-4 w-full">
          <div className="grid grid-cols-3 gap-4 mb-4 text-sm sm:text-base font-semibold text-[#222]">
            <span className="text-left" />
            <span className="text-center">Normal SIP</span>
            <span className="text-center text-primaryGreen">Top-up SIP</span>
          </div>

          <div className="space-y-4 text-sm sm:text-base font-medium text-[#222]">
            {/* Invested Amount */}
            <div className="grid grid-cols-3 gap-4">
              <span className="text-left">Invested Amount</span>
              <span className="text-center">{formatIndianNumber(Number(normalSip.totalInvestment).toFixed(0), { prefix: "₹" })}</span>
              <span className="text-center text-primaryGreen">{formatIndianNumber(Number(calculation.totalInvestment).toFixed(0), { prefix: "₹" })}</span>
            </div>

            {/* Future Value */}
            <div className="grid grid-cols-3 gap-4">
              <span className="text-left">Future Value</span>
              <span className="text-center">{formatIndianNumber(Number(normalSip.futureValue).toFixed(0), { prefix: "₹" })}</span>
              <span className="text-center text-primaryGreen">{formatIndianNumber(Number(calculation.currentBalance).toFixed(0), { prefix: "₹" })}</span>
            </div>

            {/* Profit Value */}
            <div className="grid grid-cols-3 gap-4">
              <span className="text-left">Profit Value</span>
              <span className="text-center">{formatIndianNumber(Number(normalSip.profitValue).toFixed(0), { prefix: "₹" })}</span>
              <span className="text-center text-primaryGreen">{formatIndianNumber(Number(calculation.totalReturns).toFixed(0), { prefix: "₹" })}</span>
            </div>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="lg:hidden sticky bottom-0 flex items-center rounded-b-2xl justify-around gap-3 py-3 sm:py-5 bg-white border-t border-[#e4e4e4]">
          <a href={`${ROOT_URL}start-sip.php`} className="w-full max-w-fit mx-auto bg-primaryGreen rounded-xl border border-primaryGreen text-sm font-medium text-white px-5 xs:px-14 py-2 xs:py-2.5">
            Start TOP-UP
          </a>
          <button
            onClick={() => {
              onClose?.();
              setTimeout(() => onScrollToReport?.(), 200);
            }}
            className="w-full max-w-fit mx-auto bg-white text-primaryGreen rounded-xl text-sm font-medium border border-primaryGreen px-5 xs:px-14 py-2 xs:py-2.5"
          >
            View Detail Report
          </button>
        </div>
      )}
    </>
  );
}
